import { useState, useCallback } from 'react';
import { useOpenApiClient } from './base/useOpenApiClient';
import { ErrorResponse } from 'interfaces/baseTypes';
import { components } from './base/schema';

type ProductResponse = components["schemas"]["ProductResponse"];
type ProductUpdateRequest = components["schemas"]["ProductUpdateRequest"];
export type ProductExtraFieldsUpdateRequest = components["schemas"]["ProductExtraFields"];

const useProductOpenApiClient = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<ErrorResponse | null>(null);
    const {client, getClient} = useOpenApiClient();

    const splitProduct = useCallback(async (id: number, itemNos: string[]): Promise<void> => {
        setLoading(true);
        setErrorMessage(null);
        try {
            await client?.PATCH('/api/products/{id}/split', {
                params: { path: { id } },
                body: {itemNos: itemNos}
            });
        } catch (e: any) {
            setErrorMessage(e.message || 'An error occurred');
            return undefined;
        } finally {
            setLoading(false);
        }
    }, [client]);

    const searchProduct = useCallback(async (searchTerm: string): Promise<ProductResponse[] | undefined> => {
        setLoading(true);
        setErrorMessage(null);
        try {
            const response = await client?.GET('/api/products/lookup', {
                params: { query: { searchTerm } },
                headers: { includes: 'MediaAssets' }
            });
            return response?.data || [];
        } catch (e: any) {
            setErrorMessage(e.message || 'An error occurred');
            return undefined;
        } finally {
            setLoading(false);
        }
    }, [client]);

    async function bulkUpdateProducts(productIds: number[], updateRequest: ProductUpdateRequest): Promise<ProductResponse[] | undefined> {
        setLoading(true);
        setErrorMessage(null);
        try {
            const response = await client?.PATCH('/api/products/bulk', {
                body: { productIds, updateRequest }
            });
            
            const error = response?.error as unknown as ErrorResponse;
            if (error) {
                setErrorMessage(error);
                return undefined;
            }
            
            return response?.data as ProductResponse[] | undefined;
        }
        catch (e: any) {
            setErrorMessage(e.message || 'An error occurred');
            return undefined;
        } finally {
            setLoading(false);
        }
    }
    
    return { splitProduct, searchProduct, bulkUpdateProducts, loading, errorMessage };
}



export default useProductOpenApiClient;